import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogBody,
  DialogFooter,
  DialogDescription,
} from "../../../components/ui/Dialog";
import Button from "../../../components/ui/Button";
import { Ban, AlertCircle } from "lucide-react";
import { getApiErrorMessage } from '../../../utils/apiErrors';
import {
  bulkRevoke,
  type BulkOperationResultView,
} from "../../../api/General/Subscription";

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  companyIds: number[];
  onSuccess: (result: BulkOperationResultView) => void;
}

export default function BulkRevokeDialog({ open, onOpenChange, companyIds, onSuccess }: Props) {
  const { t } = useTranslation();
  const [notes, setNotes] = useState<string>("");
  const [submitting, setSubmitting] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [touched, setTouched] = useState<boolean>(false);

  useEffect(() => {
    if (open) {
      setNotes("");
      setError(null);
      setSubmitting(false);
      setTouched(false);
    }
  }, [open]);

  const count = companyIds.length;
  const notesMissing = notes.trim().length === 0;

  const submit = async () => {
    if (count === 0) return;
    setTouched(true);
    if (notesMissing) return;
    setSubmitting(true);
    setError(null);
    try {
      const result = await bulkRevoke({ companyIds, notes: notes.trim() });
      if (result.errors && result.errors.length > 0) {
        setError(getApiErrorMessage(result, t, t("Failed to update subscription")));
        setSubmitting(false);
        return;
      }
      if (result.payload) {
        onSuccess(result.payload);
      }
      onOpenChange(false);
    } catch (e) {
      const message = getApiErrorMessage(e, t, t("Failed to update subscription"));
      setError(message);
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent size="default">
        <DialogHeader>
          <div className="flex items-start gap-3">
            <div className="shrink-0 w-9 h-9 rounded-lg bg-red-100 dark:bg-red-900/40 text-red-700 dark:text-red-300 flex items-center justify-center">
              <Ban size={18} />
            </div>
            <div className="min-w-0">
              <DialogTitle>{t("Revoke Subscription")}</DialogTitle>
              <DialogDescription className="mt-1">
                {t("{{count}} companies selected", { count })}
              </DialogDescription>
            </div>
          </div>
        </DialogHeader>

        <DialogBody className="space-y-4">
          <div className="rounded-md bg-red-50 dark:bg-red-950/30 border border-red-200 dark:border-red-900/50 p-3 text-sm text-red-800 dark:text-red-300">
            {t(
              "This will drop {{count}} companies to the Free tier immediately. All users remain active but premium features will be unavailable.",
              { count },
            )}
          </div>

          <div className="space-y-2">
            <label className="text-sm font-medium text-foreground" htmlFor="brv-notes">
              {t("Notes")} <span className="text-destructive">*</span>
            </label>
            <textarea
              id="brv-notes"
              rows={4}
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              onBlur={() => setTouched(true)}
              placeholder={t("Add a note about this change")}
              className={`flex w-full rounded-md border ${
                touched && notesMissing
                  ? "border-destructive focus-visible:ring-destructive/30"
                  : "border-input focus-visible:ring-primary/20 focus-visible:border-primary"
              } bg-background px-3 py-2 text-sm placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 transition-colors disabled:opacity-50`}
            />
            {touched && notesMissing && (
              <p className="text-xs text-destructive">{t("Notes are required for this action.")}</p>
            )}
          </div>

          {error && (
            <div className="flex items-start gap-2 rounded-md bg-red-50 dark:bg-red-950/30 border border-red-200 dark:border-red-900/50 p-3 text-sm text-red-800 dark:text-red-300">
              <AlertCircle size={16} className="mt-0.5 shrink-0" />
              <span>{error}</span>
            </div>
          )}
        </DialogBody>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={submitting}>
            {t("Cancel")}
          </Button>
          <Button
            onClick={submit}
            loading={submitting}
            variant="destructive"
            disabled={count === 0}
          >
            {t("Revoke {{count}} Subscriptions", { count })}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
